import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Rocket, GraduationCap, Loader2, CheckCircle2, Circle, TrendingUp, User2, UserPlus, Trash2, Save, Pencil, Plus,
} from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { ProgramTemplatesCatalog } from "@/components/admin/ProgramTemplatesCatalog";
import { Sparkline } from "@/components/dashboard/Sparkline";
import { MilestoneEditDialog } from "@/components/admin/MilestoneEditDialog";
import { MilestoneBoard, nextStatus } from "@/components/milestones/MilestoneBoard";

/**
 * CompanyProgramPanel — the bootcamp layer for a Company. Renders four
 * stacked sections:
 *
 *   1. Enrollment strip (pick a program, enroll/switch, graduate)
 *   2. Milestones (per-company instances, click to mark done)
 *   3. Mentors (assign existing users as advisors)
 *   4. KPIs (monthly log — MRR, users, burn, runway)
 *
 * Fed by `GET /admin/companies/{id}/program`. `onChanged` is invoked
 * after any mutation so the parent (Company detail) can reload if it
 * shows counts.
 */
export function CompanyProgramPanel({ companyId, onChanged }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [pickProgram, setPickProgram] = useState("");
  const [mentorUserId, setMentorUserId] = useState("");
  const [mentorLabel, setMentorLabel] = useState("advisor");
  const [kpi, setKpi] = useState({ month: new Date().toISOString().slice(0, 7), mrr: "", users: "", burn: "", runway_months: "", notes: "" });
  const [editingMs, setEditingMs] = useState(null); // milestone row being edited, or {} for a new one
  const [editorOpen, setEditorOpen] = useState(false);
  const [savingMs, setSavingMs] = useState(false);

  const load = useCallback(async () => {
    try {
      const [prog, progsList, usersList] = await Promise.all([
        client.get(`/admin/companies/${companyId}/program`),
        client.get(`/admin/programs`),
        client.get(`/admin/users?role=mentor`),
      ]);
      setData(prog.data);
      setPrograms(progsList.data?.items || []);
      // Advisors + mentors + admins are eligible.
      const eligibles = (usersList.data?.items || []).slice();
      // Also include advisors from a separate call.
      const advisors = await client.get(`/admin/users?role=advisor`).catch(() => ({ data: { items: [] } }));
      const seen = new Set(eligibles.map((u) => u.user_id));
      for (const u of (advisors.data?.items || [])) {
        if (!seen.has(u.user_id)) eligibles.push(u);
      }
      setUsers(eligibles);
      setPickProgram(prog.data?.company?.program_id || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load program");
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const enroll = async () => {
    if (!pickProgram) return;
    setBusy("enroll");
    try {
      const res = await client.post(`/admin/companies/${companyId}/enroll`, { program_id: pickProgram });
      toast.success(`Enrolled — ${res.data?.milestones_cloned ?? 0} milestones cloned`);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enroll failed");
    } finally { setBusy(null); }
  };

  const graduate = async () => {
    setBusy("graduate");
    try {
      await client.post(`/admin/companies/${companyId}/graduate`);
      toast.success("Marked as graduated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const patchMilestone = async (m, fields) => {
    setBusy(m.entry_id);
    try {
      await client.patch(`/admin/companies/${companyId}/milestones/${m.entry_id}`, fields);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const cycleMilestone = (m) => patchMilestone(m, { status: nextStatus(m.status) });
  const saveNote = (m, note) => patchMilestone(m, { note });

  // Swap the two rows' order values so the playbook can be resequenced
  // without a drag-and-drop dependency.
  const reorderMilestone = async (m, dir) => {
    const list = (data?.milestones || []);
    const idx = list.findIndex((x) => x.entry_id === m.entry_id);
    const other = list[idx + dir];
    if (!other) return;
    setBusy(m.entry_id);
    try {
      await client.patch(`/admin/companies/${companyId}/milestones/${m.entry_id}`, { order: other.order });
      await client.patch(`/admin/companies/${companyId}/milestones/${other.entry_id}`, { order: m.order });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const openMilestoneEditor = (row) => {
    setEditingMs(row || null); // null → create mode
    setEditorOpen(true);
  };

  const saveMilestone = async (fields) => {
    setSavingMs(true);
    try {
      if (editingMs && editingMs.entry_id) {
        await client.patch(`/admin/companies/${companyId}/milestones/${editingMs.entry_id}`, fields);
        toast.success("Milestone updated");
      } else {
        await client.post(`/admin/companies/${companyId}/milestones`, fields);
        toast.success("Milestone added");
      }
      setEditorOpen(false);
      setEditingMs(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSavingMs(false); }
  };

  const deleteMilestone = async (entry_id) => {
    setSavingMs(true);
    try {
      await client.delete(`/admin/companies/${companyId}/milestones/${entry_id}`);
      toast.success("Milestone removed");
      setEditorOpen(false);
      setEditingMs(null);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setSavingMs(false); }
  };

  const addMentor = async () => {
    if (!mentorUserId) return;
    setBusy("mentor-add");
    try {
      await client.post(`/admin/companies/${companyId}/mentors`, { user_id: mentorUserId, label: mentorLabel });
      setMentorUserId("");
      toast.success("Mentor assigned");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const removeMentor = async (entry_id) => {
    setBusy(`mentor-${entry_id}`);
    try {
      await client.delete(`/admin/companies/${companyId}/mentors/${entry_id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  const saveKpi = async (e) => {
    e?.preventDefault?.();
    setBusy("kpi-add");
    try {
      const payload = { month: kpi.month, mrr: Number(kpi.mrr || 0), users: Number(kpi.users || 0), burn: Number(kpi.burn || 0), runway_months: Number(kpi.runway_months || 0), notes: kpi.notes };
      await client.post(`/admin/companies/${companyId}/kpis`, payload);
      toast.success("KPI saved");
      setKpi({ month: new Date().toISOString().slice(0, 7), mrr: "", users: "", burn: "", runway_months: "", notes: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(null); }
  };

  const deleteKpi = async (kpi_id) => {
    setBusy(`kpi-${kpi_id}`);
    try {
      await client.delete(`/admin/companies/${companyId}/kpis/${kpi_id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(null); }
  };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const enrolled = !!data.program;
  const graduatedAt = data.company?.graduated_at;
  // Distinct stages already in use on this playbook (+ program's
  // default), so the milestone editor can offer a shared vocabulary
  // instead of free text. Kept lowercase because the backend
  // lowercases stage anyway.
  const availableStages = Array.from(new Set([
    ...(data.milestones || []).map((m) => (m.stage || "").trim().toLowerCase()).filter(Boolean),
    (data.program?.stage_default || "").trim().toLowerCase(),
  ].filter(Boolean))).sort();

  return (
    <div className="space-y-6" data-testid="admin-company-program">
      {/* Enrollment + progress */}
      <Card className="rounded-2xl border border-border p-6 space-y-4">
        <div className="flex items-start gap-3 flex-wrap">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary flex-shrink-0">
            <Rocket className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-semibold">
              Program {enrolled ? `· ${data.program.name}` : ""}
            </h3>
            <p className="text-sm text-muted-foreground">
              {enrolled
                ? graduatedAt ? "Graduated — historical record." : "Track this company against the playbook."
                : "Enroll this company in a program to load its milestone playbook."}
            </p>
          </div>
          {graduatedAt && (
            <Badge className="rounded-full bg-primary/10 text-primary border-primary/30 gap-1">
              <GraduationCap className="h-3 w-3" /> Graduated
            </Badge>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
          <Select value={pickProgram || "_none"} onValueChange={(v) => setPickProgram(v === "_none" ? "" : v)}>
            <SelectTrigger className="min-w-0 rounded-xl h-10 [&>span]:truncate [&>span]:block [&>span]:text-start" data-testid="admin-company-program-picker">
              <SelectValue placeholder="Pick a program…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— Not enrolled —</SelectItem>
              {programs.map((p) => <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="btn-brand rounded-xl gap-1 min-w-0" onClick={enroll} disabled={busy === "enroll" || !pickProgram || pickProgram === data.company.program_id} data-testid="admin-company-program-enroll">
            {busy === "enroll" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
            {enrolled ? "Switch" : "Enroll"}
          </Button>
          {enrolled && !graduatedAt && (
            <Button variant="outline" className="rounded-xl gap-1" onClick={graduate} disabled={busy === "graduate"} data-testid="admin-company-program-graduate">
              {busy === "graduate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <GraduationCap className="h-4 w-4" />}
              Graduate
            </Button>
          )}
        </div>

        {enrolled && data.progress.total > 0 && (
          <div className="pt-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Progress</span>
              <span className="font-mono" data-testid="admin-company-program-progress">
                {data.progress.done}/{data.progress.total} · {data.progress.percent}%
              </span>
            </div>
            <Progress value={data.progress.percent} className="h-2" />
          </div>
        )}
      </Card>

      {/* Templates library — only shown when not enrolled, as a one-click
          "install this playbook AND attach it to this company" shortcut. */}
      {!enrolled && (
        <ProgramTemplatesCatalog companyId={companyId} dense onInstalled={() => { load(); onChanged && onChanged(); }} />
      )}

      {/* Milestones */}
      {enrolled && (
        <Card className="rounded-2xl border border-border p-4 sm:p-6 space-y-4" data-testid="admin-company-milestones">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              {t("milestones.heading", { defaultValue: "Milestones" })}
            </h3>
            {data.milestone_stats?.overdue > 0 && (
              <Badge variant="outline" className="rounded-full border-rose-500/40 bg-rose-500/10 text-rose-600 text-[10px]" data-testid="admin-company-milestones-overdue">
                {data.milestone_stats.overdue} overdue
              </Badge>
            )}
          </div>
          {data.milestones.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">This program has no milestones yet — add one below or in the Programs panel.</p>
          ) : null}
          <MilestoneBoard
            milestones={data.milestones}
            stats={data.milestone_stats}
            busyKey={busy}
            onCycle={cycleMilestone}
            onEdit={openMilestoneEditor}
            onAdd={() => openMilestoneEditor(null)}
            onReorder={reorderMilestone}
            onNote={saveNote}
          />
        </Card>
      )}

      <MilestoneEditDialog
        open={editorOpen}
        onOpenChange={(v) => { setEditorOpen(v); if (!v) setEditingMs(null); }}
        milestone={editingMs}
        onSave={saveMilestone}
        onDelete={deleteMilestone}
        saving={savingMs}
        availableStages={availableStages}
      />

      {/* Mentors */}
      <Card className="rounded-2xl border border-border p-6 space-y-3" data-testid="admin-company-mentors">
        <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">Mentors & Advisors</h3>
        <div className="grid gap-3 sm:grid-cols-[1fr_180px_auto]">
          <Select value={mentorUserId || "_none"} onValueChange={(v) => setMentorUserId(v === "_none" ? "" : v)}>
            <SelectTrigger className="rounded-xl h-10" data-testid="admin-company-mentor-picker">
              <SelectValue placeholder={users.length === 0 ? "No mentors/advisors in system yet" : "Pick a user…"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">—</SelectItem>
              {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email} · {u.role}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input value={mentorLabel} onChange={(e) => setMentorLabel(e.target.value)} placeholder="Label — advisor, lead" className="rounded-xl" />
          <Button className="btn-brand rounded-xl gap-1" onClick={addMentor} disabled={!mentorUserId || busy === "mentor-add"} data-testid="admin-company-mentor-add">
            {busy === "mentor-add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            Assign
          </Button>
        </div>
        {data.mentors.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No mentors assigned yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {data.mentors.map((m) => (
              <li key={m.entry_id} className="flex items-center gap-3 py-2" data-testid={`admin-company-mentor-${m.entry_id}`}>
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-muted"><User2 className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{m.name || m.email}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{m.label} · {m.email}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => removeMentor(m.entry_id)} aria-label="Remove mentor">
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Evaluation */}
      {data.evaluation && (
        <Card className="rounded-2xl border border-border p-6 space-y-3" data-testid="admin-company-evaluation">
          <div className="flex items-center gap-2 flex-wrap">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">Evaluation</h3>
            <span className="ml-auto flex items-center gap-2">
              <span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${
                data.evaluation.grade === "A" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" :
                data.evaluation.grade === "B" ? "bg-primary/10 text-primary border-primary/30" :
                data.evaluation.grade === "C" ? "bg-amber-500/10 text-amber-600 border-amber-500/30" :
                                                "bg-rose-500/10 text-rose-600 border-rose-500/30"
              }`} data-testid="admin-company-evaluation-grade">
                Grade {data.evaluation.grade}
              </span>
              <span className="text-lg font-mono font-semibold" data-testid="admin-company-evaluation-score">
                {data.evaluation.score}/100
              </span>
            </span>
          </div>
          <p className="text-xs text-muted-foreground italic">{data.evaluation.recommendation}</p>
          <ul className="grid gap-2 sm:grid-cols-2">
            {data.evaluation.signals.map((s) => (
              <li key={s.key} className="flex items-start gap-2 rounded-lg border border-border p-2">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground w-16 flex-shrink-0">
                  {s.key.replace(/_/g, " ")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-xs">{s.note}</p>
                </div>
                <span className="text-xs font-mono text-muted-foreground">+{s.score}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* KPIs */}
      <Card className="rounded-2xl border border-border p-6 space-y-3" data-testid="admin-company-kpis">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h3 className="font-display text-sm font-semibold uppercase tracking-widest text-muted-foreground">Monthly KPIs</h3>
        </div>
        <form onSubmit={saveKpi} className="grid gap-3 sm:grid-cols-6">
          <div className="sm:col-span-2">
            <Label className="text-xs">Month</Label>
            <Input type="month" value={kpi.month} onChange={(e) => setKpi({ ...kpi, month: e.target.value })} className="rounded-xl mt-1" data-testid="admin-company-kpi-month" />
          </div>
          <div>
            <Label className="text-xs">MRR</Label>
            <Input value={kpi.mrr} onChange={(e) => setKpi({ ...kpi, mrr: e.target.value })} className="rounded-xl mt-1" placeholder="0" data-testid="admin-company-kpi-mrr" />
          </div>
          <div>
            <Label className="text-xs">Users</Label>
            <Input value={kpi.users} onChange={(e) => setKpi({ ...kpi, users: e.target.value })} className="rounded-xl mt-1" placeholder="0" />
          </div>
          <div>
            <Label className="text-xs">Burn</Label>
            <Input value={kpi.burn} onChange={(e) => setKpi({ ...kpi, burn: e.target.value })} className="rounded-xl mt-1" placeholder="0" />
          </div>
          <div>
            <Label className="text-xs">Runway (mo)</Label>
            <Input value={kpi.runway_months} onChange={(e) => setKpi({ ...kpi, runway_months: e.target.value })} className="rounded-xl mt-1" placeholder="0" />
          </div>
          <div className="sm:col-span-6">
            <Label className="text-xs">Notes</Label>
            <Input value={kpi.notes} onChange={(e) => setKpi({ ...kpi, notes: e.target.value })} className="rounded-xl mt-1" placeholder="One line summary of this month" />
          </div>
          <div className="sm:col-span-6 flex justify-end">
            <Button type="submit" className="btn-brand rounded-xl gap-1" disabled={busy === "kpi-add"} data-testid="admin-company-kpi-save">
              {busy === "kpi-add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save month
            </Button>
          </div>
        </form>
        {data.kpis.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No KPI history yet.</p>
        ) : (
          <>
            {/* Sparkline strip — chronological order for readability. */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2" data-testid="admin-company-kpi-sparks">
              {[
                { key: "mrr", label: "MRR", invert: false },
                { key: "users", label: "Users", invert: false },
                { key: "burn", label: "Burn", invert: true },
                { key: "runway_months", label: "Runway (mo)", invert: false },
              ].map((m) => {
                const series = data.kpis.slice().reverse().map((k) => Number(k[m.key]) || 0);
                const latest = series[series.length - 1] || 0;
                const first = series[0] || 0;
                const delta = first ? Math.round(((latest - first) / Math.abs(first)) * 100) : 0;
                const good = m.invert ? delta <= 0 : delta >= 0;
                return (
                  <div key={m.key} className="rounded-xl border border-border p-3" data-testid={`admin-company-kpi-spark-${m.key}`}>
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{m.label}</p>
                    <div className="flex items-baseline justify-between mt-1">
                      <span className="font-mono text-sm">
                        {m.key === "users" ? latest.toLocaleString() : latest.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                      </span>
                      {series.length > 1 && (
                        <span className={`text-[10px] font-mono ${good ? "text-emerald-500" : "text-rose-500"}`}>
                          {delta > 0 ? "+" : ""}{delta}%
                        </span>
                      )}
                    </div>
                    <div className="mt-1"><Sparkline values={series} width={140} height={26} invert={m.invert} /></div>
                  </div>
                );
              })}
            </div>

            <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-widest text-muted-foreground">
                  <th className="py-2 px-2">Month</th>
                  <th className="py-2 px-2">MRR</th>
                  <th className="py-2 px-2">Users</th>
                  <th className="py-2 px-2">Burn</th>
                  <th className="py-2 px-2">Runway</th>
                  <th className="py-2 px-2">Notes</th>
                  <th className="py-2 px-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.kpis.map((k) => (
                  <tr key={k.kpi_id} data-testid={`admin-company-kpi-${k.kpi_id}`}>
                    <td className="py-2 px-2 font-mono">{k.month}</td>
                    <td className="py-2 px-2">{k.mrr.toLocaleString()}</td>
                    <td className="py-2 px-2">{k.users.toLocaleString()}</td>
                    <td className="py-2 px-2">{k.burn.toLocaleString()}</td>
                    <td className="py-2 px-2">{k.runway_months}</td>
                    <td className="py-2 px-2 max-w-[220px] truncate text-muted-foreground">{k.notes}</td>
                    <td className="py-2 px-2 text-right">
                      <Button size="icon" variant="ghost" onClick={() => deleteKpi(k.kpi_id)} aria-label="Delete KPI">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </Card>
    </div>
  );
}

export default CompanyProgramPanel;
